import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

const WaiterRequestTracker = ({ customerMobile, customerName, restaurantId }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (customerMobile && restaurantId) {
            fetchRequests();
        }
    }, [customerMobile, restaurantId]);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.CUSTOMER_WAITER_REQUESTS(customerMobile, restaurantId));
            setRequests(response.data);
        } catch (error) {
            console.error('Error fetching waiter requests:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'initiated': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'process': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'done': return 'bg-green-100 text-green-800 border-green-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'initiated': return '🔔';
            case 'process': return '⏳';
            case 'done': return '✅';
            default: return '❓';
        }
    };

    const formatDateTime = (value) => {
        if (!value) return '';
        try {
            return new Date(value).toLocaleString();
        } catch {
            return '';
        }
    };

    if (!customerMobile) {
        return (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 shadow-sm p-4 text-center">
                <p className="text-amber-800 font-medium">
                    Please provide your mobile number to track waiter requests
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 sm:p-4">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="text-lg sm:text-xl font-bold text-stone-900 flex items-center gap-2">
                            <span>🔔</span>
                            My Waiter Requests
                        </h3>
                        {customerName && (
                            <p className="text-stone-600 text-sm mt-1">
                                👤 {customerName} • 📱 {customerMobile}
                            </p>
                        )}
                        <p className="text-xs text-stone-500 mt-1">
                            {requests?.length ? `${requests.length} request${requests.length === 1 ? '' : 's'} tracked` : 'No requests yet'}
                        </p>
                    </div>

                    <button
                        onClick={fetchRequests}
                        disabled={loading}
                        className="inline-flex items-center justify-center rounded-full border border-stone-200 bg-white px-3 py-1 text-[11px] sm:text-xs font-semibold text-stone-600 hover:bg-stone-50 transition-colors disabled:bg-stone-100 disabled:text-stone-400"
                        aria-label="Refresh waiter requests"
                    >
                        {loading ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-10 bg-white rounded-xl border border-slate-200">
                    <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="text-stone-600 mt-2 font-medium">Loading requests…</p>
                </div>
            ) : requests.length === 0 ? (
                <div className="text-center py-10 bg-white rounded-xl border border-slate-200">
                    <div className="text-4xl mb-2">🔔</div>
                    <p className="text-stone-700 font-semibold">No waiter requests yet</p>
                    <p className="text-stone-500 text-sm mt-1">
                        Use the “Call Waiter” button when you need assistance
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {requests.map((request) => (
                        <div
                            key={request._id}
                            className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="min-w-0">
                                    <h4 className="font-semibold text-stone-900 flex items-center gap-2">
                                        <span>🪑</span>
                                        <span className="truncate">
                                            Table {request.tableId?.tableNumber ?? '—'}
                                        </span>
                                    </h4>
                                    <p className="text-stone-500 text-sm mt-1">
                                        {formatDateTime(request.createdAt)}
                                    </p>
                                </div>

                                <span
                                    className={`shrink-0 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold border ${getStatusColor(
                                        request.status
                                    )}`}
                                    title={`Status: ${request.status}`}
                                >
                                    {getStatusIcon(request.status)} {String(request.status || '').toUpperCase()}
                                </span>
                            </div>

                            <div className="space-y-2">
                                <div className="bg-stone-50 border border-stone-200 p-3 rounded-xl">
                                    <p className="text-stone-800 text-sm">
                                        <span className="font-semibold">Your Request:</span>{' '}
                                        <span className="text-stone-700">{request.message || '—'}</span>
                                    </p>
                                </div>

                                {request.reply ? (
                                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-xl">
                                        <p className="text-blue-800 text-sm">
                                            <span className="font-semibold">Staff Reply:</span>{' '}
                                            <span className="text-blue-900/90">{request.reply}</span>
                                        </p>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default WaiterRequestTracker;